/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import ReactCrop from "react-easy-crop";
import {
  uploadBannerImage,
  fetchBannerImage,
  vendorFetchSubscriptionApi,
} from "../../../Services/Vendor/VendorApi/VendorApi";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateRealtorAdBanner } from "../../../Services/Realtor/RealtorApi/RealtorApi";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useDropzone } from "react-dropzone";

// Utility function to crop the image and return a Data URL
const getCroppedImg = (imageSrc, pixelCrop) => {
  const image = new Image();
  image.src = imageSrc;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Setting canvas size
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  return new Promise((resolve) => {
    image.onload = () => {
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      // Resolve with the base64 image
      resolve(canvas.toDataURL("image/jpeg"));
    };
  });
};

const Dropzone = ({ handleFileChange }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [],
    },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const event = { target: { files: [file] } };
      handleFileChange(event);
    },
  });

  return (
    <div {...getRootProps()} className="upload-box">
      <input {...getInputProps()} />
      <div>
        {isDragActive ? (
          <p>Drop the image here</p>
        ) : (
          <div className="d-flex flex-column align-items-center">
            <FileUploadOutlinedIcon fontSize="large" color="action" />
            <p>Drag & drop an image here, or click to browse</p>
            <label htmlFor="fileInput" className="upload-btn-1">
              Select from device
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

function UploadBannerModal({ id }) {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("user_id");
  const [show, setShow] = useState(false); // For the upload modal
  const [showCropModal, setShowCropModal] = useState(false); // For the crop modal
  const [image, setImage] = useState(null); // Image to crop
  const [crop, setCrop] = useState({ x: 0, y: 0 }); // Crop position
  const [zoom, setZoom] = useState(1); // Zoom level for crop tool
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null); // Crop area in pixels
  const [selectedImage, setSelectedImage] = useState(null); // Store only one selected image
  const [imageUrl, setImageUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { data } = useSelector((state) => state.fetchBannerImage);
  const handleClose = () => setShow(false);
  const handleCloseCropModal = () => {
    setShowCropModal(false);
    setImage(null);
    setImageUrl("");
    setUrlError("");
    handleShow(); // Reopen the upload modal
  };
  const handleShow = () => setShow(true);
  const handleShowCropModal = () => setShowCropModal(true);

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
      handleClose(); // Close the upload modal first
      handleShowCropModal(); // Then open the crop modal
    };
    if (file) reader.readAsDataURL(file);
  };

  // Handle cropping area change
  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels); // Update cropped area in pixels
  };

  // Crop the image when user clicks Save

  const handleSave = async () => {
    if (image && croppedAreaPixels) {
      if (!imageUrl.trim()) {
        setUrlError("URL is required.");
        return; // Don't proceed if URL is empty
      }

      const croppedImage = await getCroppedImg(image, croppedAreaPixels);

      // Create a new FormData object
      const formData = new FormData();

      const byteString = atob(croppedImage.split(",")[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([uint8Array], { type: "image/jpeg" });

      formData.append("ads_banner_image", blob, `${new Date().getTime()}.jpg`);
      formData.append("ads_banner_url", imageUrl);
      formData.append("advertisement_banner_id", id);

      //

      try {
        setIsSaving(true);
        // Dispatch the API call and wait for the result
        const resultAction = await dispatch(updateRealtorAdBanner(formData));

        // Check if the action was successful (fulfilled)
        if (updateRealtorAdBanner.fulfilled.match(resultAction)) {
          // If successful, handle the response
          toast.success("Image uploaded successfully");
          dispatch(fetchBannerImage(userId));
          dispatch(vendorFetchSubscriptionApi(userId));

          // Just close the crop modal without reopening the upload modal
          setShowCropModal(false);
          setImage(null);
          setImageUrl("");
          setUrlError("");
        } else {
          // If failed, handle the error
          console.error("Upload failed:", resultAction.payload);
        }
        setIsSaving(false);
      } catch (error) {
        setIsSaving(false);
        console.error("Error in handleSave:", error);
      }
    }
  };

  const handleSelectedImageUpload = async () => {
    if (!selectedImage || !data?.data[selectedImage]) {
      return;
    }

    const selectedImg = data?.data[selectedImage];

    try {
      setIsSaving(true);

      // Convert image URL to Blob
      const response = await fetch(selectedImg.ads_banner_image, {
        mode: "cors",
      });
      const blob = await response.blob();
      const file = new File([blob], "uploaded_image.jpg", { type: blob.type });

      const formData = new FormData();
      formData.append("ads_banner_image", file); // Append file instead of URL
      formData.append("vendor_subscriber", id);
      formData.append("id", selectedImg?.id);

      const resultAction = await dispatch(uploadBannerImage(formData));

      if (uploadBannerImage.fulfilled.match(resultAction)) {
        dispatch(fetchBannerImage(userId)); // Refresh uploaded images
        setSelectedImage(null);
        setImageUrl("");
        handleClose();
      } else {
        console.error("Upload failed:", resultAction.payload);
      }
    } catch (error) {
      console.error("Error in handleSelectedImageUpload:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <button className="bttn-close rounded-pill" onClick={handleShow}>
        AD Banner
      </button>

      {/* Upload Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload an Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="upload-box">
              <Dropzone handleFileChange={handleFileChange} />
            </div>
          </Form>

          {/* Display previously uploaded images */}
          <div className="uploaded-images">
            <div className="uploaded-images d-flex flex-column gap-4 mt-4">
              {/* <h6>Select an image and click save.</h6> */}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="bttn-close" onClick={handleClose}>
            Close
          </button>
          <button
            className="bttn-upload"
            onClick={handleSelectedImageUpload}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Crop Modal */}
      <Modal show={showCropModal} onHide={handleCloseCropModal}>
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ position: "relative", width: "100%", height: "300px" }}>
            {image && (
              <ReactCrop
                image={image}
                crop={crop}
                zoom={zoom}
                minWidth={1600}
                minHeight={400}
                aspect={1600 / 400}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            )}
          </div>
          <div style={{ marginTop: "20px" }}>
            <label htmlFor="urlInput">Image URL:</label>
            <input
              type="text"
              id="urlInput"
              className="form-control"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Enter image URL"
            />
            {urlError && (
              <p style={{ color: "red", marginTop: "10px" }}>{urlError}</p>
            )}{" "}
            {/* Error message */}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="bttn-close" onClick={handleCloseCropModal}>
            Close
          </button>
          <button
            className="bttn-upload"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Cropped Image"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UploadBannerModal;
